import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiEnvelope,
  HiUser,
  HiChatBubbleLeftRight,
  HiPaperAirplane,
  HiCheckCircle,
} from 'react-icons/hi2';

const initialState = { name: '', email: '', message: '' };

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 24 },
  },
};

const ContactUsForm = () => {
  const [form, setForm] = useState(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setTimeout(() => {
        setForm(initialState);
        setSubmitted(false);
      }, 2000);
    }, 1500);
  };

  return (
    <section className="relative overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-xl shadow-gray-200/50 md:rounded-3xl">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white to-emerald-50/30" />
      <div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-blue-400/10 blur-3xl" />
      <div className="absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-emerald-400/10 blur-3xl" />

      <div className="relative mx-auto max-w-xl px-4 py-12 sm:px-8 md:py-16 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10 text-center sm:mb-12"
        >
          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25 ring-4 ring-blue-100">
            <HiEnvelope className="h-7 w-7" />
          </div>
          <h2 className="mb-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Contact Us
          </h2>
          <p className="mx-auto max-w-md text-base leading-relaxed text-gray-600 sm:text-lg">
            We&apos;d love to hear from you. Fill out the form below and we&apos;ll get back to you
            within 24 hours.
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="flex flex-col items-center py-12 text-center sm:py-16"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-xl shadow-emerald-500/30"
              >
                <HiCheckCircle className="h-10 w-10" />
              </motion.div>
              <h3 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl">Message Sent!</h3>
              <p className="max-w-sm text-gray-600">
                Thanks for reaching out. We&apos;ll get back to you within 24 hours.
              </p>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              onSubmit={handleSubmit}
              className="space-y-5 sm:space-y-6"
            >
              <motion.div variants={itemVariants} className="relative">
                <HiUser
                  className={`absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transition-colors ${
                    focusedField === 'name' ? 'text-blue-600' : 'text-gray-400'
                  }`}
                />
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField('')}
                  placeholder="Your Name"
                  required
                  className={`w-full rounded-xl border-2 py-3.5 pl-12 pr-4 text-sm transition-all duration-200 focus:outline-none focus:ring-4 sm:py-4 sm:text-base ${
                    focusedField === 'name'
                      ? 'border-blue-500 bg-white shadow-md shadow-blue-500/5 ring-blue-500/20'
                      : 'border-gray-200 bg-gray-50/80 hover:border-gray-300 hover:bg-white focus:border-blue-500'
                  } text-gray-900 placeholder-gray-400`}
                />
              </motion.div>

              <motion.div variants={itemVariants} className="relative">
                <HiEnvelope
                  className={`absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transition-colors ${
                    focusedField === 'email' ? 'text-blue-600' : 'text-gray-400'
                  }`}
                />
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField('')}
                  placeholder="your@email.com"
                  required
                  className={`w-full rounded-xl border-2 py-3.5 pl-12 pr-4 text-sm transition-all duration-200 focus:outline-none focus:ring-4 sm:py-4 sm:text-base ${
                    focusedField === 'email'
                      ? 'border-blue-500 bg-white shadow-md shadow-blue-500/5 ring-blue-500/20'
                      : 'border-gray-200 bg-gray-50/80 hover:border-gray-300 hover:bg-white focus:border-blue-500'
                  } text-gray-900 placeholder-gray-400`}
                />
              </motion.div>

              <motion.div variants={itemVariants} className="relative">
                <HiChatBubbleLeftRight
                  className={`absolute left-4 top-4 h-5 w-5 transition-colors ${
                    focusedField === 'message' ? 'text-blue-600' : 'text-gray-400'
                  }`}
                />
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField('')}
                  placeholder="Tell us what's on your mind..."
                  required
                  rows={4}
                  className={`w-full resize-none rounded-xl border-2 py-3.5 pl-12 pr-4 text-sm transition-all duration-200 focus:outline-none focus:ring-4 sm:py-4 sm:text-base ${
                    focusedField === 'message'
                      ? 'border-blue-500 bg-white shadow-md shadow-blue-500/5 ring-blue-500/20'
                      : 'border-gray-200 bg-gray-50/80 hover:border-gray-300 hover:bg-white focus:border-blue-500'
                  } text-gray-900 placeholder-gray-400`}
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={loading}
                  className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-base font-semibold text-white shadow-lg shadow-blue-500/30 transition-all duration-200 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl hover:shadow-blue-500/40 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:shadow-lg"
                >
                  <motion.span
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '100%' }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent group-disabled:hidden"
                  />
                  <span className="relative flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="h-5 w-5 rounded-full border-2 border-white/30 border-t-white"
                        />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <HiPaperAirplane className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        <span>Send Message</span>
                      </>
                    )}
                  </span>
                </motion.button>
              </motion.div>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ContactUsForm;
